import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PackagePlus, Search, XCircle } from 'lucide-react';
import apiClient from '../../utils/api';
import { useToast } from '../../context/ToastContext';

const STATUS_OPTIONS = ['', 'PENDING', 'CONFIRMED', 'DEPARTED', 'IN_TRANSIT', 'DELIVERED', 'CANCELLED'];
const LIMIT = 15;

export const UserShipments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [shipments, setShipments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (search) filters.trackingNumber = search;
      const res = await apiClient.listShipments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setShipments(d?.shipments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const cancelShipment = async (id: string) => {
    if (!window.confirm('Cancel this shipment? This cannot be undone.')) return;
    setCancellingId(id);
    try {
      await apiClient.cancelShipment(id);
      toast('Shipment cancelled.', 'success');
      load();
    } catch (err: any) {
      toast(err.response?.data?.error || 'Failed to cancel shipment.', 'error');
    } finally {
      setCancellingId('');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">My Shipments</h1>
        <Link to="/user/shipments/create" className="btn btn-primary"><PackagePlus size={16} /> Send Package</Link>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search tracking number..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 180 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
        </div>

        {loading ? <div className="loading-spinner">Loading...</div> : shipments.length === 0 ? (
          <div className="empty-state">
            <h3>No shipments yet</h3>
            <p><Link to="/user/shipments/create">Send your first package</Link></p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Tracking #</th><th>Receiver</th><th>City</th><th>Status</th><th>Amount</th><th>Date</th><th></th></tr>
                </thead>
                <tbody>
                  {shipments.map((s: any) => (
                    <tr key={s.id}>
                      <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                      <td>{s.receiverName}</td>
                      <td>{s.receiverCity || '—'}</td>
                      <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{Number(s.totalAmount).toLocaleString()} RWF</td>
                      <td style={{ fontSize: 12 }}>{new Date(s.createdAt).toLocaleDateString()}</td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <Link to={`/user/shipments/${s.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link>
                        <Link to={`/user/tracking?q=${s.trackingNumber}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>Track</Link>
                        {['PENDING', 'CONFIRMED'].includes(s.status) && (
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '3px 8px', fontSize: 12, color: '#ef4444' }}
                            disabled={cancellingId === s.id}
                            onClick={() => cancelShipment(s.id)}
                          >
                            <XCircle size={12} /> {cancellingId === s.id ? '...' : 'Cancel'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
